import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Download, FileCheck2, Loader2, MonitorSmartphone } from "lucide-react";
import { errorFields, errorMessage } from "../../hooks/useCommission";
import { fmtDate } from "../proposal/format";
import { ClientPaperStep } from "./ClientPaperStep";
import { ConfirmStep, ExpiredRequested } from "./ConfirmStep";
import { MedicysSowStep } from "./MedicysSowStep";
import { RouteStep } from "./RouteStep";
import { Stepper } from "./Stepper";
import { Notice, StepHeader } from "./fields";
import { VersionHistory } from "./VersionHistory";

const DONE_CARD = "rounded-xl border p-6 sm:p-8 flex flex-col gap-6";

function Processing({ route }) {
  const ours = route !== "client_paper";
  return (
    <div className="flex flex-col gap-8" data-testid="commission-processing">
      <StepHeader eyebrow={`Step 4 of 5 · ${ours ? "Signing" : "Sending"}`} title={ours ? "Preparing your signed Statement of Work" : "Sending your contract details"} />
      <div className="rounded-xl border border-stone-200 bg-white p-8 sm:p-10 flex flex-col items-center text-center gap-5">
        <span className="relative flex w-16 h-16 items-center justify-center">
          <span className="absolute inset-0 rounded-full border-4 border-[#3EA8DC]/20" />
          <span className="absolute inset-0 rounded-full border-4 border-[#3EA8DC] border-t-transparent animate-spin" />
          <FileCheck2 className="w-6 h-6 text-[#1B4F8A]" />
        </span>
        <p className="text-[15px] text-stone-700 max-w-md leading-relaxed">
          {ours
            ? "Your signature is recorded. We are rendering the signed PDF, filing it with Medicys and emailing your copy. This takes a few moments; you can close this page if you wish, your copy arrives by email either way."
            : "Your answers are recorded. We are filing them with Medicys and letting your contact know. This takes a few moments; you can close this page if you wish."}
        </p>
        <div className="w-full max-w-xs h-1.5 rounded-full bg-stone-200 overflow-hidden">
          <div className="h-full w-1/2 bg-[#1B4F8A] rounded-full animate-[shimmer_1.6s_ease-in-out_infinite]" style={{ animationName: "commission-progress" }} />
        </div>
      </div>
      <style>{`@keyframes commission-progress { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }`}</style>
    </div>
  );
}

function SignedDone({ state, executedUrl, versionUrl }) {
  const sig = state.signature || {};
  const csig = state.countersignature || {};
  const awaiting = !state.executed_pdf && sig.signed_at && state.stage !== "signed_elsewhere";
  const version = state.version || 1;
  return (
    <div className="flex flex-col gap-8" data-testid="commission-signed-done" data-version={version}>
      <StepHeader eyebrow="Step 5 of 5 · Done" title={awaiting ? "Thank you, your signature has been received" : "Thank you, the Statement of Work is signed"} />
      <div className={`${DONE_CARD} border-emerald-200 bg-emerald-50/70`}>
        {version > 1 && (
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-800" data-testid="commission-version-label">
            Version {version}{state.amended?.issued_at ? ` · amended ${fmtDate(state.amended.issued_at)}` : state.amendment?.issued_at ? ` · amended ${fmtDate(state.amendment.issued_at)}` : ""}
          </p>
        )}
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0"><CheckCircle2 className="w-6 h-6" /></span>
          <div className="text-[15px] text-emerald-950 leading-relaxed">
            {awaiting ? (
              <p data-testid="commission-awaiting-countersign">Signed by <span className="font-semibold">{sig.name}</span> on {fmtDate(sig.signed_at)}. The project is in our schedule. Medicys is now countersigning the document; the fully executed copy, with both signatures and the signature certificate, will be emailed to {sig.email} and will also be available to download from this page once countersigned.</p>
            ) : sig.signed_at ? (
              <p>Signed by <span className="font-semibold">{sig.name}</span> on {fmtDate(sig.signed_at)}{csig.signed_at ? <> and countersigned for Medicys by <span className="font-semibold">{csig.name}</span> on {fmtDate(csig.signed_at)}</> : null}. The project is commissioned and the fully executed copy has been emailed to {sig.email}.</p>
            ) : (
              <p>The Statement of Work for this proposal is signed and on record with Medicys. The project is commissioned.</p>
            )}
          </div>
        </div>
        {state.executed_pdf && (
          <a href={executedUrl} className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1B4F8A] hover:bg-[#143E72] text-white font-semibold px-6 py-3 text-[15px] transition-colors self-start" data-testid="commission-download-executed">
            <Download className="w-4 h-4" /> Download the signed Statement of Work{version > 1 ? ` (version ${version})` : ""}
          </a>
        )}
      </div>
      <VersionHistory versions={state.versions} versionUrl={versionUrl} />
    </div>
  );
}

function ClientPaperDone({ state }) {
  const cp = state.client_paper || {};
  return (
    <div className="flex flex-col gap-8" data-testid="commission-client-paper-done">
      <StepHeader eyebrow="Step 5 of 5 · Done" title="Thank you, your contract details are with Medicys" />
      <div className={`${DONE_CARD} border-sky-200 bg-sky-50/70`}>
        <div className="flex items-start gap-4">
          <span className="w-12 h-12 rounded-full bg-[#1B4F8A] text-white flex items-center justify-center shrink-0"><CheckCircle2 className="w-6 h-6" /></span>
          <div className="text-[15px] text-sky-950 leading-relaxed flex flex-col gap-2">
            <p>The project is commissioned{cp.submitted_at ? ` and your details were recorded on ${fmtDate(cp.submitted_at)}` : ""}.</p>
            {cp.drafts?.length > 0 ? (
              <p>We have received {cp.drafts.length === 1 ? "your draft" : `${cp.drafts.length} files`}: {cp.drafts.map((d) => d.filename).join(", ")}. Medicys is reviewing it against the proposal and will come back with comments.</p>
            ) : (
              <p>You told us the draft contract will follow by email. Please send it to {state.bd_name || "your Medicys contact"} when it is ready.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Orchestrates the commissioning steps on the public viewer, keyed on
// the server side stage. Answers auto save a moment after each change.
export function CommissionPanel({ d, commission }) {
  const { state, error: loadError, start, chooseRoute, saveAnswers, sign, submitClientPaper, uploadDraft, removeDraft, requestDeadlineCheck, requestChange, previewUrl, executedUrl, versionUrl } = commission;
  const [answers, setAnswers] = useState(null);
  const [invalid, setInvalid] = useState(() => new Set());
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [error, setError] = useState(null);
  const [pickingRoute, setPickingRoute] = useState(false);
  const timer = useRef(null);
  const pending = useRef({});

  const flush = useCallback(async () => {
    const batch = pending.current;
    pending.current = {};
    if (!Object.keys(batch).length || !state?.can_write) return;
    setSaving(true);
    try {
      await saveAnswers(batch);
    } catch (_e) {
      // Draft save is best effort, the final submit carries every answer.
    } finally {
      setSaving(false);
    }
  }, [saveAnswers, state?.can_write]);

  useEffect(() => {
    if (!state || answers) return;
    const saved = state.answers || {};
    const prefill = Object.fromEntries(Object.entries(state.prefill || {}).filter(([, v]) => v !== null && v !== undefined && v !== ""));
    setAnswers({ ...prefill, ...saved });
    const unsaved = Object.fromEntries(Object.entries(prefill).filter(([k]) => saved[k] === undefined || saved[k] === null));
    if (state.can_write && Object.keys(unsaved).length) {
      pending.current = { ...pending.current, ...unsaved };
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, 900);
    }
  }, [state, answers, flush]);

  const set = (k, v) => {
    setAnswers((a) => ({ ...(a || {}), [k]: v }));
    pending.current[k] = v;
    if (invalid.has(k)) setInvalid((s) => { const n = new Set(s); n.delete(k); return n; });
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, k === "project_deadline" || k === "planned_start" ? 300 : 900);
  };

  // Saves whatever is pending first, so the server assesses the deadline shown on screen.
  const deadlineCheck = async () => {
    setChecking(true);
    setError(null);
    try {
      clearTimeout(timer.current);
      await flush();
      await requestDeadlineCheck();
    } catch (e) {
      setError(errorMessage(e, "We could not send the request. Please try again."));
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const run = async (fn, fallback) => {
    setBusy(true);
    setError(null);
    setInvalid(new Set());
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, fallback));
      const fields = errorFields(e);
      if (fields.length) {
        setInvalid(new Set(fields));
        setTimeout(() => document.querySelector('[data-invalid="true"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return <Notice tone="info" testid="commission-unavailable">{loadError}</Notice>;
  }
  if (!state || !answers) {
    return <div className="flex items-center gap-2 text-sm text-stone-500 py-10 justify-center" data-testid="commission-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading</div>;
  }
  if (state.closed) {
    return <Notice tone="info" testid="commission-closed">This proposal is closed in our records. Please contact your Medicys contact if you would like to revisit it.</Notice>;
  }

  const { stage } = state;
  const onBack = () => setPickingRoute(true);
  const pick = (route) => run(async () => { await chooseRoute(route); setPickingRoute(false); }, "We could not record your choice. Please try again.");
  const formStage = ["open", "medicys_sow", "sign_failed", "client_paper", "client_paper_failed"].includes(stage);

  let body;
  if (stage === "open") {
    body = <ConfirmStep state={state} d={d} busy={busy} error={error} onStart={() => run(() => start(), "We could not record your request. Please try again.")} />;
  } else if (stage === "expired_request") {
    body = <ExpiredRequested state={state} />;
  } else if (stage === "signed" || stage === "signed_elsewhere" || stage === "client_signed" || stage === "countersigning" || stage === "countersign_failed") {
    body = <SignedDone state={state} executedUrl={executedUrl} versionUrl={versionUrl} />;
  } else if (stage === "client_paper_submitted") {
    body = <ClientPaperDone state={state} />;
  } else if (stage === "signing" || stage === "client_paper_submitting") {
    body = <Processing route={state.route} />;
  } else if (stage === "commissioned" || (pickingRoute && !state.amendment)) {
    body = <RouteStep onChoose={pick} busy={busy} />;
  } else if (stage === "medicys_sow" || stage === "sign_failed") {
    body = (
      <MedicysSowStep
        state={state}
        answers={answers}
        set={set}
        busy={busy}
        error={error}
        invalid={invalid}
        checking={checking}
        onDeadlineCheck={deadlineCheck}
        saving={saving}
        previewUrl={previewUrl}
        onBack={state.amendment ? null : onBack}
        amendment={state.amendment}
        returned={state.returned}
        versions={state.versions}
        versionUrl={versionUrl}
        onRequestChange={async (text) => { clearTimeout(timer.current); await flush(); await requestChange(text); }}
        onSign={(sigBody) => run(async () => {
          clearTimeout(timer.current);
          pending.current = {};
          await sign({ answers, ...sigBody });
        }, "Signing did not go through. Please try again.")}
      />
    );
  } else if (stage === "client_paper" || stage === "client_paper_failed") {
    body = (
      <ClientPaperStep
        state={state}
        answers={answers}
        set={set}
        busy={busy}
        error={error}
        invalid={invalid}
        checking={checking}
        onDeadlineCheck={deadlineCheck}
        uploading={uploading}
        uploadError={uploadError}
        onBack={onBack}
        onUpload={async (file) => {
          setUploading(true);
          setUploadError(null);
          try {
            await uploadDraft(file);
            setError(null);
          } catch (e) {
            setUploadError(errorMessage(e, "The upload did not complete. Please try again."));
          } finally {
            setUploading(false);
          }
        }}
        onRemove={(id) => removeDraft(id).catch(() => null)}
        onSubmit={() => run(async () => {
          clearTimeout(timer.current);
          pending.current = {};
          await submitClientPaper(answers);
        }, "Your details did not go through. Please try again.")}
      />
    );
  } else {
    body = <Notice tone="info">{state.stage_label || "Commissioning is in progress."}</Notice>;
  }

  return (
    <div className="flex flex-col gap-8 sm:gap-10 animate-in fade-in-0 slide-in-from-bottom-2 duration-500" data-testid="commission-panel" data-stage={stage}>
      {!state.can_write && stage !== "open" && (
        <div className="flex items-center gap-2.5 rounded-md bg-[#0B1220] text-stone-200 px-4 py-2.5 text-xs font-mono" data-testid="commission-staff-banner">
          <MonitorSmartphone className="w-3.5 h-3.5 text-[#3EA8DC]" /> Staff preview. This is what the client sees; the buttons only work with a verified client email.
        </div>
      )}
      <Stepper stage={pickingRoute ? "commissioned" : stage} route={state.route} />
      {error && !formStage && <Notice tone="danger" testid="commission-error">{error}</Notice>}
      <div key={pickingRoute ? "routes" : stage} className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300">{body}</div>
    </div>
  );
}
